/**
 * FF Article Product Slider - Web Component for product sliders in article content
 *
 * Uses Shopify Storefront API to fetch product data including metafields,
 * enabling full badge parity with ff-product-card.liquid.
 *
 * Attributes (on component):
 * - data-product-ids: Comma-separated product IDs or handles
 * - data-collection: Collection handle or numeric ID to fetch products from
 * - data-limit: Max products to show (default: 12)
 *
 * Parent container (.ff-article-content) attributes:
 * - data-slider-per-page: Items per view on desktop (default: 3)
 * - data-slider-per-page-mb: Items per view on mobile (default: 1.2)
 * - data-mobile-breakpoint: Mobile breakpoint in px (default: 768)
 * - data-new-in-days: Days threshold for "New" badge (equiv. to settings.ff_new_in_days, default: 30)
 * - data-storefront-token: Storefront API public access token
 *                          (alt. to window.Shopify.storefrontAccessToken)
 *
 * Metafields must be enabled for Storefront API access in Shopify admin:
 *   - fantastic.badges          (list.metaobject_reference)
 *   - magento.best_seller       (single_line_text_field)
 *   - magento.entrega_estimada  (any type — solo se verifica si existe)
 *   - shopify.lens-polarization (list.metaobject_reference)
 * Metaobjects of type "fantastic_badge" must be enabled for Storefront API access.
 */

if (!customElements.get('ff-article-product-slider')) {
  class FFArticleProductSlider extends HTMLElement {
    constructor() {
      super();
      this._initialized = false;
      this.products = [];
      this.limit = 12;
      // Lookup map: { 'badge-handle': { text, highlighted, icon_url, border_type } }
      this.fallbackBadges = {};
    }

    connectedCallback() {
      if (!this._initialized) {
        this.init();
      }
    }

    async init() {
      if (this._initialized) return;
      this._initialized = true;

      this.limit = parseInt(this.dataset.limit) || 12;
      this.innerHTML = '<div class="ff-article-slider__loading">Cargando productos...</div>';

      try {
        // Fetch fallback badges from shop metaobjects (equiv. shop.metaobjects.fantastic_badge en Liquid)
        await this.fetchFallbackBadges();

        if (this.dataset.productIds) {
          await this.fetchProductsByIds(this.dataset.productIds);
        } else if (this.dataset.collection) {
          await this.fetchProductsByCollection(this.dataset.collection);
        }

        if (this.products.length > 0) {
          this.render();
        } else {
          this.innerHTML = '';
        }
      } catch (error) {
        console.error('FF Article Slider Error:', error);
        this.innerHTML = '';
      }
    }

    // ─── Storefront API ──────────────────────────────────────────────────────

    /**
     * Resolve Storefront API credentials from data attributes or window globals
     */
    getStorefrontConfig() {
      const token =
        this.dataset.storefrontToken ||
        this.closest('.ff-article-content')?.dataset.storefrontToken ||
        window.Shopify?.storefrontAccessToken ||
        window.shopifyStorefrontToken;

      const shop = window.Shopify?.shop || window.location.hostname;

      return {
        token,
        endpoint: `https://${shop}/api/2025-01/graphql.json`
      };
    }

    async storefrontQuery(query, variables = {}) {
      const { token, endpoint } = this.getStorefrontConfig();

      if (!token) {
        throw new Error(
          'FF Article Slider: Storefront API token not found. ' +
          'Set window.Shopify.storefrontAccessToken or data-storefront-token on .ff-article-content.'
        );
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token
        },
        body: JSON.stringify({ query, variables })
      });

      if (!response.ok) {
        throw new Error(`Storefront API HTTP error: ${response.status}`);
      }

      const json = await response.json();
      if (json.errors?.length) {
        throw new Error(`Storefront API GraphQL errors: ${json.errors.map(e => e.message).join(', ')}`);
      }

      return json.data;
    }

    // ─── Product fetching ────────────────────────────────────────────────────

    /**
     * Shared product fields used in all product queries.
     * Metafields require storefront access to be enabled per namespace in Shopify admin.
     */
    get productFields() {
      return `
        id
        title
        handle
        vendor
        availableForSale
        createdAt
        onlineStoreUrl
        priceRange {
          minVariantPrice { amount currencyCode }
        }
        compareAtPriceRange {
          minVariantPrice { amount currencyCode }
        }
        featuredImage {
          url(transform: { maxWidth: 600 })
          altText
        }
        images(first: 2) {
          nodes {
            url(transform: { maxWidth: 600 })
            altText
          }
        }
        variants(first: 100) {
          nodes {
            quantityAvailable
          }
        }
        metafields(identifiers: [
          { namespace: "fantastic", key: "badges" }
          { namespace: "magento", key: "best_seller" }
          { namespace: "magento", key: "entrega_estimada" }
          { namespace: "shopify", key: "lens-polarization" }
        ]) {
          namespace
          key
          value
          type
          references(first: 10) {
            nodes {
              ... on Metaobject {
                handle
                type
                fields {
                  key
                  value
                  type
                  reference {
                    ... on MediaImage {
                      image { url(transform: { maxWidth: 24 }) altText }
                    }
                  }
                }
              }
            }
          }
        }
      `;
    }

    /**
     * Fetch shop metaobjects of type fantastic_badge for fallback badge logic.
     * Equivalente a shop.metaobjects.fantastic_badge['handle'] en Liquid.
     */
    async fetchFallbackBadges() {
      const query = `
        query FallbackBadges {
          metaobjects(type: "fantastic_badge", first: 20) {
            nodes {
              handle
              type
              fields {
                key
                value
                type
                reference {
                  ... on MediaImage {
                    image { url(transform: { maxWidth: 24 }) altText }
                  }
                }
              }
            }
          }
        }
      `;

      try {
        const data = await this.storefrontQuery(query);
        (data?.metaobjects?.nodes || []).forEach(node => {
          this.fallbackBadges[node.handle] = this.parseMetaobjectFields(node);
        });
      } catch (e) {
        // Non-fatal: badge fallbacks won't be available but products still render
        console.warn('FF Article Slider: Could not fetch fallback badges:', e.message);
      }
    }

    async fetchProductsByIds(idsString) {
      const ids = idsString.split(',').map(id => id.trim()).filter(Boolean);
      const fetchPromises = ids.slice(0, this.limit).map(id => this.fetchProduct(id));
      const results = await Promise.allSettled(fetchPromises);

      this.products = results
        .filter(r => r.status === 'fulfilled' && r.value)
        .map(r => r.value);
    }

    async fetchProduct(handleOrId) {
      // Numeric ID → Shopify GID format for Storefront API
      if (/^\d+$/.test(handleOrId)) {
        return this.fetchProductByGid(`gid://shopify/Product/${handleOrId}`);
      }
      return this.fetchProductByHandle(handleOrId);
    }

    async fetchProductByHandle(handle) {
      const query = `
        query ProductByHandle($handle: String!) {
          product(handle: $handle) { ${this.productFields} }
        }
      `;
      try {
        const data = await this.storefrontQuery(query, { handle });
        return data?.product ? this.normalizeProduct(data.product) : null;
      } catch (e) {
        console.warn('FF Article Slider: Could not fetch product by handle:', handle);
        return null;
      }
    }

    async fetchProductByGid(id) {
      const query = `
        query ProductById($id: ID!) {
          product(id: $id) { ${this.productFields} }
        }
      `;
      try {
        const data = await this.storefrontQuery(query, { id });
        return data?.product ? this.normalizeProduct(data.product) : null;
      } catch (e) {
        console.warn('FF Article Slider: Could not fetch product by ID:', id);
        return null;
      }
    }

    async fetchProductsByCollection(handleOrId) {
      // Los posts antiguos referencian la colección por ID numérico; la Storefront
      // API la resuelve por id (GID) o por handle, así que soportamos ambos.
      const isId = /^\d+$/.test(handleOrId);
      const query = isId
        ? `
          query CollectionProductsById($id: ID!, $limit: Int!) {
            collection(id: $id) {
              products(first: $limit) {
                nodes { ${this.productFields} }
              }
            }
          }
        `
        : `
          query CollectionProducts($handle: String!, $limit: Int!) {
            collection(handle: $handle) {
              products(first: $limit) {
                nodes { ${this.productFields} }
              }
            }
          }
        `;
      const variables = isId
        ? { id: `gid://shopify/Collection/${handleOrId}`, limit: this.limit }
        : { handle: handleOrId, limit: this.limit };

      const data = await this.storefrontQuery(query, variables);
      if (!data?.collection) {
        throw new Error(`Collection not found: ${handleOrId}`);
      }
      this.products = (data.collection.products?.nodes || [])
        .map(p => this.normalizeProduct(p));
    }

    // ─── Data normalization ──────────────────────────────────────────────────

    /**
     * Normalize a Storefront API product node to the internal format.
     * Prices are converted from decimal strings to cents to keep formatMoney compatible.
     */
    normalizeProduct(sfProduct) {
      if (!sfProduct) return null;

      const price = Math.round(parseFloat(sfProduct.priceRange.minVariantPrice.amount) * 100);
      const compareAtFloat = parseFloat(sfProduct.compareAtPriceRange.minVariantPrice.amount);
      // compareAtPriceRange returns "0.00" when no variant has a compare price
      const compare_at_price = compareAtFloat > 0 ? Math.round(compareAtFloat * 100) : null;

      // Metafields → lookup map { 'namespace.key': metafield }
      const mfMap = {};
      (sfProduct.metafields || []).forEach(mf => {
        if (mf) mfMap[`${mf.namespace}.${mf.key}`] = mf;
      });

      // fantastic.badges → array of parsed metaobjects
      const badges = (mfMap['fantastic.badges']?.references?.nodes || [])
        .map(node => this.parseMetaobjectFields(node))
        .filter(Boolean);

      // shopify.lens-polarization → array of handles (e.g. ['polarizada', 'fotocromatica'])
      const lensPolarization = (mfMap['shopify.lens-polarization']?.references?.nodes || [])
        .map(n => n.handle);

      // Mirrors inventory_quantity > 0 check in Liquid: avoids showing delivery badge
      // on products with sell-when-out-of-stock enabled but zero real inventory.
      const hasRealStock = (sfProduct.variants?.nodes || [])
        .some(v => v.quantityAvailable !== null && v.quantityAvailable > 0);

      return {
        id:               sfProduct.id,
        title:            sfProduct.title,
        handle:           sfProduct.handle,
        vendor:           sfProduct.vendor,
        available:        sfProduct.availableForSale,
        hasRealStock,
        price,
        compare_at_price,
        // Storefront API applies size transform in query — use URLs directly
        featured_image:   sfProduct.featuredImage?.url || null,
        images:           sfProduct.images.nodes.map(img => img.url),
        url:              `${window.Shopify.routes.root || '/'}products/${sfProduct.handle}`,
        created_at:       sfProduct.createdAt,
        // Parsed metafields
        badges,
        best_seller:      mfMap['magento.best_seller']?.value?.trim().toLowerCase() || null,
        entrega_estimada: mfMap['magento.entrega_estimada']?.value || null,
        lens_polarization: lensPolarization
      };
    }

    /**
     * Flatten a Storefront API metaobject fields array into a plain object.
     * Output: { handle, type, text, highlighted, icon_url, border_type }
     */
    parseMetaobjectFields(node) {
      if (!node) return null;
      const obj = { handle: node.handle, type: node.type };
      (node.fields || []).forEach(({ key, value, reference }) => {
        switch (key) {
          case 'text':        obj.text = value; break;
          case 'highlighted': obj.highlighted = value === 'true'; break;
          case 'border_type': obj.border_type = value; break;
          case 'icon':        obj.icon_url = reference?.image?.url || null; break;
        }
      });
      return obj;
    }

    // ─── Slider settings ─────────────────────────────────────────────────────

    getSliderSettings() {
      const container = this.closest('.ff-article-content');
      if (!container) return { perPage: 3, perPageMb: 1.2, breakpoint: 768, newInDays: 30, hideNewBadge: false, hideDiscountBadge: false };

      return {
        perPage:            parseFloat(container.dataset.sliderPerPage) || 3,
        perPageMb:          parseFloat(container.dataset.sliderPerPageMb) || 1.2,
        breakpoint:         parseInt(container.dataset.mobileBreakpoint) || 768,
        newInDays:          parseInt(container.dataset.newInDays) || 30,
        hideNewBadge:       container.dataset.hideNewBadge === 'true',
        hideDiscountBadge:  container.dataset.hideDiscountBadge === 'true',
        deliveryBadgeText:  container.dataset.deliveryBadgeText || '24/48h'
      };
    }

    // ─── Rendering ───────────────────────────────────────────────────────────

    render() {
      const uniqueClass = `ff-article-slider-${this.generateId()}`;
      const slidesHtml = this.products.map(product => this.renderProductSlide(product)).join('');
      const settings = this.getSliderSettings();

      this.innerHTML = `
        <div class="ff-article-slider">
          <ff-slider class="${uniqueClass} ff-slider--loading">
            <section class="splide" data-splide='{
              "type": "slide",
              "perPage": ${settings.perPage},
              "gap": "16px",
              "breakpoints": {
                "${settings.breakpoint}": {
                  "perPage": ${settings.perPageMb},
                  "gap": "12px"
                }
              }
            }'>
              <div class="splide__track">
                <ul class="splide__list">
                  ${slidesHtml}
                </ul>
              </div>
              <div class="splide__arrows">
                <button class="splide__arrow splide__arrow--prev ff-button" type="button">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </button>
                <button class="splide__arrow splide__arrow--next ff-button" type="button">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </button>
              </div>
            </section>
          </ff-slider>
        </div>
      `;

      const slider = this.querySelector('ff-slider');
      if (slider && typeof slider.init === 'function') {
        slider.init();
      }
    }

    renderProductSlide(product) {
      const price = this.formatMoney(product.price);
      const comparePrice = product.compare_at_price ? this.formatMoney(product.compare_at_price) : null;
      const onSale = product.compare_at_price && product.compare_at_price > product.price;
      // Images already sized via Storefront API transform parameter — use URLs directly
      const imageUrl = product.featured_image || '';
      const secondaryImage = product.images?.[1] || '';
      const topBadges = this.renderTopBadges(product, onSale);
      const bottomBadges = this.renderBottomBadges(product);

      return `
        <li class="splide__slide">
          <a href="${product.url}" class="ff-product-card ff-d-grid ff-article-slider__card">
            <div class="ff-product-card__image-wrapper ff-relative ff-overflow-hidden">
              ${imageUrl ? `
                <img
                  src="${imageUrl}"
                  alt="${this.escapeHtml(product.title)}"
                  class="ff-product-card__image ff-w-full ff-h-full ff-cover"
                  loading="lazy"
                  width="600"
                  height="600"
                >
              ` : `
                <div class="ff-product-card__image ff-placeholder ff-w-full ff-h-full">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5"><path d="M324.5 212.7H203c-1.6 0-2.8 1.3-2.8 2.8V308c0 1.6 1.3 2.8 2.8 2.8h121.6c1.6 0 2.8-1.3 2.8-2.8v-92.5c0-1.5-1.3-2.8-2.9-2.8zm1.1 95.3c0 .6-.5 1.1-1.1 1.1H203c-.6 0-1.1-.5-1.1-1.1v-92.5c0-.6.5-1.1 1.1-1.1h121.6c.6 0 1.1.5 1.1 1.1V308z"/></svg>
                </div>
              `}
              ${secondaryImage ? `
                <img
                  src="${secondaryImage}"
                  alt="${this.escapeHtml(product.title)}"
                  class="ff-product-card__image ff-product-card__image--secondary ff-absolute ff-w-full ff-h-full ff-cover"
                  loading="lazy"
                  width="600"
                  height="600"
                >
              ` : ''}
              <div class="ff-product-card__badges ff-absolute ff-d-flex ff-align-start ff-flex-wrap">
                ${topBadges}
              </div>
              ${bottomBadges ? `
                <div class="ff-product-card__badges ff-product-card__badges--bottom ff-absolute ff-d-flex ff-flex-wrap ff-justify-end ff-align-end">
                  ${bottomBadges}
                </div>
              ` : ''}
            </div>
            <div class="ff-product-card__content ff-d-grid ff-align-start">
              ${product.vendor ? `<div class="ff-product-card__vendor ff-snippet-text ff-text-ellipsis ff-text-nowrap">${this.escapeHtml(product.vendor)}</div>` : ''}
              <h3 class="ff-product-card__title ff-snippet-text ff-overflow-hidden ff-text-ellipsis ff-text-nowrap">${this.escapeHtml(product.title)}</h3>
              <div class="ff-product-card__price ff-snippet-text${onSale ? ' ff-product-card__price--sale' : ''}">${price}</div>
              ${onSale ? `<div class="ff-product-card__compare-price ff-snippet-text ff-text-d-line-through">${comparePrice}</div>` : ''}
            </div>
          </a>
        </li>
      `;
    }

    /**
     * Render top badges — mirrors the ff-product-card__badges container in Liquid.
     *
     * Order matches ff-product-card.liquid:
     * 1. Discount (compare_at_price > price)
     * 2. Soldout (not available + no entrega_estimada)
     * 3. fantastic.badges (excludes 24-48-h + polarizadas/fotocromaticas/gafas-con-ia)
     * 4. Fallback: 24-48h delivery badge (only if real inventory > 0, via quantityAvailable)
     * 5. Fallback: 'new' badge by created_at date
     * 6. Fallback: bestseller (magento.best_seller == 'si' | 'yes')
     *
     * ⚠️ Liquid also checks collection.metafields.custom.show_discount_percentage for discount badge
     *    — no collection context available client-side.
     */
    renderTopBadges(product, onSale) {
      const settings = this.getSliderSettings();
      let badges = '';

      // 1. Discount badge
      if (onSale && !settings.hideDiscountBadge) {
        const discountPercent = Math.round(
          (product.compare_at_price - product.price) / product.compare_at_price * 100
        );
        if (discountPercent > 0) {
          badges += this.renderBadge(`${discountPercent}% off`, null, 'discount');
        }
      }

      // 2. Soldout badge (solo si no tiene entrega estimada)
      if (!product.available && !product.entrega_estimada) {
        const soldOutText = window.Shopify?.strings?.soldOut || 'Sold out';
        badges += this.renderBadge(soldOutText, null, 'soldout', 'ff-text-t-uppercase');
      }

      // 3. Feature/Highlight badges desde fantastic.badges
      //    (gafas-con-ia, fotocromaticas, polarizadas van al contenedor inferior)
      const BOTTOM_HANDLES = new Set(['gafas-con-ia', 'fotocromaticas', 'polarizadas']);
      const hasBadge = {};
      for (const badge of (product.badges || [])) {
        hasBadge[badge.handle] = true;
        if (!BOTTOM_HANDLES.has(badge.handle) && badge.handle !== '24-48-h' && badge.text) {
          const proto = badge.highlighted ? 'highlight' : 'feature';
          badges += this.renderBadge(badge.text, badge.icon_url, proto, 'ff-text-t-uppercase', badge.border_type);
        }
      }

      // 4. Delivery badge: only when real inventory > 0 (ignores sell-when-out-of-stock)
      if (product.hasRealStock) {
        const fb = this.fallbackBadges['24-48-h'];
        const text = (fb?.text) || settings.deliveryBadgeText;
        const icon = fb?.icon_url || null;
        const borderType = fb?.border_type || '';
        badges += this.renderBadge(text, icon, 'feature', 'ff-article-slider__badge--delivery ff-text-t-uppercase', borderType);
      }

      // 5. Fallback: 'new' badge — equiv. a ff_difference_days <= settings.ff_new_in_days
      if (!hasBadge['new'] && !settings.hideNewBadge && product.created_at) {
        const diffDays = Math.floor((Date.now() - new Date(product.created_at)) / 86400000);
        if (diffDays <= settings.newInDays) {
          const fb = this.fallbackBadges['new'];
          const text = fb?.text || 'NEW';
          const icon = fb?.icon_url || null;
          const proto = fb?.highlighted ? 'highlight' : 'feature';
          const borderType = fb?.border_type || '';
          badges += this.renderBadge(text, icon, proto, 'ff-text-t-uppercase', borderType);
        }
      }

      // 6. Fallback: bestseller desde magento.best_seller metafield
      if (!hasBadge['bestseller'] && (product.best_seller === 'si' || product.best_seller === 'yes')) {
        const fb = this.fallbackBadges['bestseller'];
        if (fb?.text) {
          const proto = fb.highlighted ? 'highlight' : 'feature';
          badges += this.renderBadge(fb.text, fb.icon_url, proto, 'ff-text-t-uppercase', fb.border_type);
        }
      }

      return badges;
    }

    /**
     * Render bottom badges (polarizadas, fotocromaticas).
     * Mirrors ff-product-card__badges--bottom container in Liquid.
     *
     * Sources (with priority):
     * 1. fantastic.badges with handle 'polarizadas' or 'fotocromaticas'
     * 2. Fallback from shopify.lens-polarization metafield handles
     */
    renderBottomBadges(product) {
      let badges = '';
      const LENS_TO_BADGE = { polarizada: 'polarizadas', fotocromatica: 'fotocromaticas' };
      const hasBadge = {};

      // Source 1: fantastic.badges (same 3 handles as ff-product-card bottom container)
      const BOTTOM_HANDLES = new Set(['polarizadas', 'fotocromaticas', 'gafas-con-ia']);
      for (const badge of (product.badges || [])) {
        if (BOTTOM_HANDLES.has(badge.handle) && badge.text) {
          const proto = badge.highlighted ? 'highlight' : 'feature';
          badges += this.renderBadge(badge.text, badge.icon_url, proto, 'ff-text-t-uppercase', badge.border_type);
          hasBadge[badge.handle] = true;
        }
      }

      // Source 2: shopify.lens-polarization fallback
      for (const lensHandle of (product.lens_polarization || [])) {
        const badgeHandle = LENS_TO_BADGE[lensHandle];
        if (badgeHandle && !hasBadge[badgeHandle]) {
          const fb = this.fallbackBadges[badgeHandle];
          if (fb?.text) {
            const proto = fb.highlighted ? 'highlight' : 'feature';
            badges += this.renderBadge(fb.text, fb.icon_url, proto, 'ff-text-t-uppercase', fb.border_type);
          }
        }
      }

      return badges;
    }

    /**
     * Render a single badge element matching ff-badge snippet HTML output:
     * <div class="ff-snippet-badge ff-product-card__badge ff-product-card__badge--{prototype} ...">
     *   [<img class="ff-badge__icon">] <span>text</span>
     * </div>
     */
    renderBadge(text, iconUrl, prototype, additionalClasses = '', borderType = '') {
      const borderTypeClass = borderType === 'Bottom & left' ? ' ff-snippet-badge--border-bottom-left' : '';
      const icon = iconUrl
        ? `<img src="${iconUrl}" alt="" width="24" height="24" class="ff-badge__icon" loading="lazy">`
        : '';
      const content = icon && text
        ? `${icon}<span>${this.escapeHtml(text)}</span>`
        : icon || `<span>${this.escapeHtml(text)}</span>`;

      return `<div class="ff-snippet-badge ff-article-slider__badge--${prototype} ff-product-card__badge ff-product-card__badge--${prototype}${borderTypeClass} ${additionalClasses}">${content}</div>`;
    }

    // ─── Utilities ────────────────────────────────────────────────────────────

    /**
     * Format price in cents to currency string.
     * Prices are normalized to cents in normalizeProduct() to keep this method compatible.
     */
    formatMoney(cents) {
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(cents);
      }
      const currency = window.Shopify?.currency?.active || 'EUR';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'es-ES', {
          style: 'currency',
          currency
        }).format(cents / 100);
      } catch (e) {
        return `${(cents / 100).toFixed(2)} ${currency}`;
      }
    }

    generateId() {
      return Math.random().toString(36).substring(2, 9);
    }

    escapeHtml(str) {
      if (!str) return '';
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
  }

  customElements.define('ff-article-product-slider', FFArticleProductSlider);
}
